/* eslint-disable react/prop-types */
import { Formik, Form } from "formik";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Import component UI form cho Sổ Chi Phí Đầu Tư Xây Dựng (S52-DN)
import ProjectCostLedgerInfo from "./info";

import { ProjectCostLedgerService } from "services/project-cost-ledger";
import { getPrintTemplate } from "./components/printTemplate";

const today = new Date();

// 1. Initial values khớp DTO CreateValueAddedTaxRefundDto
const initialValues = {
  companyAddress: "",
  year: today.getFullYear().toString(),
  projectName: "",
  constructionName: "",
  startDate: "",
  completionDate: "",
  totalEstimatedBudget: "",
  openingBalance: {
    totalAmount: "",
    constructionAmount: "",
    equipmentTotal: "",
    equipmentRequireInstallation: "",
    equipmentNoInstallation: "",
    toolsAmount: "",
    otherAmount: "",
  },
  items: [],
  totalPages: "",
  openingDate: "",
  signingDay: today.getDate().toString(),
  signingMonth: (today.getMonth() + 1).toString(),
  signingYear: today.getFullYear().toString(),
  preparerName: "",
  chiefAccountantName: "",
  legalRepresentativeName: "",
  status: "OPEN",
};

function ProjectCostLedgerDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();
  const [getData, setGetData] = useState({});
  const [data, setData] = useState({});

  const [formInitials, setFormInitials] = useState(initialValues);

  // 2. Load data từ API và map vào form
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const response = await ProjectCostLedgerService.getById(id);
        const apiData = response.data ? response.data : response;

        // Map items: VatRefundTransaction schema
        const mappedItems = (apiData.items || []).map((item) => ({
          recordingDate: item.recordingDate
            ? new Date(item.recordingDate).toLocaleDateString("vi-VN")
            : "",
          documentNumber: item.documentNumber || "",
          documentDate: item.documentDate
            ? new Date(item.documentDate).toLocaleDateString("vi-VN")
            : "",
          description: item.description || "",
          correspondingAccount: item.correspondingAccount || "",
          costs: item.costs || {
            totalAmount: "",
            constructionAmount: "",
            equipmentTotal: "",
            equipmentRequireInstallation: "",
            equipmentNoInstallation: "",
            toolsAmount: "",
            otherAmount: "",
          },
          note: item.note || "",
          totalAmount: item.costs?.totalAmount || "",
          constructionAmount: item.costs?.constructionAmount || "",
          equipmentTotal: item.costs?.equipmentTotal || "",
          equipmentRequireInstallation: item.costs?.equipmentRequireInstallation || "",
          equipmentNoInstallation: item.costs?.equipmentNoInstallation || "",
          toolsAmount: item.costs?.toolsAmount || "",
          otherAmount: item.costs?.otherAmount || "",
        }));

        // Parse signingDate
        let sDay = initialValues.signingDay;
        let sMonth = initialValues.signingMonth;
        let sYear = initialValues.signingYear;
        if (apiData.signingDate) {
          const sDate = new Date(apiData.signingDate);
          sDay = sDate.getDate().toString();
          sMonth = (sDate.getMonth() + 1).toString();
          sYear = sDate.getFullYear().toString();
        }

        const loadedData = {
          ...initialValues,
          company: apiData.company?.name || apiData.company || "",
          companyAddress: apiData.companyAddress || "",
          year: apiData.year?.toString() || initialValues.year,
          projectName: apiData.projectName || "",
          constructionName: apiData.constructionName || "",
          startDate: apiData.startDate
            ? new Date(apiData.startDate).toLocaleDateString("vi-VN")
            : "",
          completionDate: apiData.completionDate
            ? new Date(apiData.completionDate).toLocaleDateString("vi-VN")
            : "",
          totalEstimatedBudget: apiData.totalEstimatedBudget || "",
          openingBalance: apiData.openingBalance || initialValues.openingBalance,
          totalArising: apiData.totalArising || {},
          closingBalance: apiData.closingBalance || {},
          cumulativeQuarter: apiData.cumulativeQuarter || {},
          cumulativeYear: apiData.cumulativeYear || {},
          cumulativeFromStart: apiData.cumulativeFromStart || {},
          items: mappedItems.length > 0 ? mappedItems : [],
          totalPages: apiData.totalPages || "",
          openingDate: apiData.openingDate
            ? new Date(apiData.openingDate).toLocaleDateString("vi-VN")
            : "",
          signingDay: apiData.signingDay?.toString() || initialValues.signingDay,
          signingMonth: apiData.signingMonth?.toString() || initialValues.signingMonth,
          signingYear: apiData.signingYear?.toString() || initialValues.signingYear,
          preparerName: apiData.preparerName || "",
          chiefAccountantName: apiData.chiefAccountantName || "",
          legalRepresentativeName: apiData.legalRepresentativeName || "",
          status: apiData.status || "OPEN",
        };

        setFormInitials(loadedData);
        setGetData(apiData);
      } catch (error) {
        console.error("Lỗi getById Sổ Chi Phí Đầu Tư Xây Dựng:", error);
        toast.error("Không thể tải dữ liệu");
      }
    })();
  }, [id]);

  const onChangeValue = useCallback((type, value) => {
    setData((prev) => ({ ...prev, [type]: value }));
  }, []);

  const handleBack = () => navigation(-1);

  // 3. Submit payload 100% khớp CreateValueAddedTaxRefundDto
  const submitForm = async (values) => {
    try {
      const num = (val) => (val && !isNaN(val) ? Number(val) : 0);

      const getValue = (field) => (data[field] !== undefined ? data[field] : values[field]);
      const itemsFlat = getValue("items") || [];

      // Build items theo VatRefundTransaction schema
      const buildItems = (itemsList) =>
        itemsList.map((item) => ({
          recordingDate: item.recordingDate ? new Date(item.recordingDate) : null,
          documentNumber: item.documentNumber || "",
          documentDate: item.documentDate ? new Date(item.documentDate) : null,
          description: item.description || "",
          correspondingAccount: item.correspondingAccount || "",
          costs: {
            totalAmount: num(item.costs?.totalAmount),
            constructionAmount: num(item.costs?.constructionAmount),
            equipmentTotal: num(item.costs?.equipmentTotal),
            equipmentRequireInstallation: num(item.costs?.equipmentRequireInstallation),
            equipmentNoInstallation: num(item.costs?.equipmentNoInstallation),
            toolsAmount: num(item.costs?.toolsAmount),
            otherAmount: num(item.costs?.otherAmount),
          },
          note: item.note || "",
        }));

      // Build signingDate ISO
      const signD = getValue("signingDay");
      const signM = getValue("signingMonth");
      const signY = getValue("signingYear");
      let signingDate = null;
      if (signY && signM && signD) {
        signingDate = new Date(
          `${signY}-${String(signM).padStart(2, "0")}-${String(signD).padStart(2, "0")}`
        ).toISOString();
      }

      // Payload chính xác theo DTO
      const payload = {
        companyAddress: getValue("companyAddress") || "",
        year: num(getValue("year")),
        projectName: getValue("projectName") || "",
        constructionName: getValue("constructionName") || "",
        startDate: getValue("startDate") ? new Date(getValue("startDate")) : null,
        completionDate: getValue("completionDate") ? new Date(getValue("completionDate")) : null,
        totalEstimatedBudget: num(getValue("totalEstimatedBudget")),
        openingBalance: getValue("openingBalance") || {},
        items: buildItems(itemsFlat),
        cumulativeQuarter: getValue("cumulativeQuarter") || {},
        cumulativeYear: getValue("cumulativeYear") || {},
        cumulativeFromStart: getValue("cumulativeFromStart") || {},
        totalPages: num(getValue("totalPages")),
        openingDate: getValue("openingDate") ? new Date(getValue("openingDate")) : null,
        signingDate,
        preparerName: getValue("preparerName") || "",
        chiefAccountantName: getValue("chiefAccountantName") || "",
        legalRepresentativeName: getValue("legalRepresentativeName") || "",
        status: getValue("status") || "OPEN",
      };

      console.log("✅ ProjectCostLedger Payload:", JSON.stringify(payload, null, 2));

      if (!id) {
        await ProjectCostLedgerService.create(payload);
        toast.success("Tạo Sổ Chi Phí Đầu Tư Xây Dựng thành công!");
      } else {
        await ProjectCostLedgerService.update(id, payload);
        toast.success("Cập nhật thành công!");
      }
    } catch (error) {
      console.error("Lỗi lưu Sổ Chi Phí Đầu Tư Xây Dựng:", error);
      toast.error(error?.response?.data?.message || "Lưu thất bại!");
      throw error;
    }
  };

  const handlePrintDirect = (values) => {
    const finalData = { ...values, ...data };
    const htmlContent = getPrintTemplate({
      headerInfo: finalData,
      rows: finalData.items || [],
      openingBalance: finalData.openingBalance || {},
      totalArising: finalData.totalArising || {},
      closingBalance: finalData.closingBalance || {},
      cumulativeQuarter: finalData.cumulativeQuarter || {},
      cumulativeYear: finalData.cumulativeYear || {},
      cumulativeFromStart: finalData.cumulativeFromStart || {},
    });

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const handleSubmit = async (values, actions) => {
    try {
      await submitForm(values);
    } catch (error) {
      // Error đã handle trong submitForm
    } finally {
      if (actions) {
        actions.setTouched({});
        actions.setSubmitting(false);
      }
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={20}>
        <Grid container justifyContent="center" sx={{ height: "100%" }}>
          <Grid item xs={12} lg={12}>
            <Formik initialValues={formInitials} enableReinitialize={true} onSubmit={handleSubmit}>
              {({ values, errors, touched, isSubmitting, setFieldValue }) => (
                <Form autoComplete="off">
                  <Card sx={{ height: "100%" }}>
                    <SoftBox p={2}>
                      <SoftBox>
                        <ProjectCostLedgerInfo
                          formData={{
                            values,
                            onChangeValue,
                          }}
                        />

                        <SoftBox mt={10} width="100%" display="flex" justifyContent="space-between">
                          <SoftButton variant="gradient" color="light" onClick={handleBack}>
                            Trở về
                          </SoftButton>

                          {containView ? (
                            <SoftBox display="flex" gap={3}>
                              <SoftButton
                                variant="gradient"
                                color="success"
                                onClick={() => handlePrintDirect(values)}
                                type="button"
                              >
                                In Sổ Chi Phí Đầu Tư Xây Dựng (S52-DN)
                              </SoftButton>
                            </SoftBox>
                          ) : (
                            <SoftBox display="flex" gap={3}>
                              <SoftButton
                                disabled={isSubmitting}
                                type="submit"
                                variant="gradient"
                                color="dark"
                              >
                                {id ? "Lưu" : "Tạo"}
                              </SoftButton>
                            </SoftBox>
                          )}
                        </SoftBox>
                      </SoftBox>
                    </SoftBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default ProjectCostLedgerDetail;
